import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes } from '@aws-amplify/auth';
import { getMSPTenants, getMSPUsers } from '../utils/mspApi';
import MSPSidebar from './MSPSidebar';
import MSPTopbar from './MSPTopbar';
import MSPFinOpsV2 from './MSPFinOpsV2';
import MSPSecurity from './MSPSecurity';
import MSPCloudMap from './MSPCloudMap';
import MSPCompanies from './MSPCompanies';
import toastr from 'toastr';

const MSPDashboard = ({ mspConfig }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadDashboardData = async () => {
    try {
      await getCurrentUser();
      const attributes = await fetchUserAttributes();
      setCurrentUser({ attributes });

      const [tenantsData, usersData] = await Promise.all([
        getMSPTenants(mspConfig.mspID),
        getMSPUsers(mspConfig.mspID)
      ]);

      setTenants(tenantsData.tenants || []);
      setUsers(usersData.users || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toastr.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <MSPSidebar 
        isVisible={isSidebarOpen} 
        toggleSidebar={toggleSidebar}
        mspConfig={mspConfig}
        tenantCount={tenants.length}
        userCount={users.length}
      />

      <div
        className="main-content"
        style={{
          marginLeft: isSidebarOpen ? '250px' : '80px',
          transition: 'margin-left 0.3s ease',
        }}
      >
        <MSPTopbar 
          isSidebarOpen={isSidebarOpen} 
          toggleSidebar={toggleSidebar}
          currentUser={currentUser}
          mspConfig={mspConfig}
        />
        
        <div style={{ paddingTop: '80px' }}>
          <Routes>
            <Route path="/" element={<MSPOverview tenants={tenants} users={users} mspConfig={mspConfig} isSidebarOpen={isSidebarOpen} />} />
            <Route path="/tenants" element={<MSPTenants tenants={tenants} mspConfig={mspConfig} isSidebarOpen={isSidebarOpen} />} />
            <Route path="/companies" element={<MSPCompanies mspConfig={mspConfig} tenants={tenants} isSidebarOpen={isSidebarOpen} />} />
            <Route path="/finops" element={<MSPFinOpsV2 mspConfig={mspConfig} isSidebarOpen={isSidebarOpen} />} />
            <Route path="/security" element={<MSPSecurity mspConfig={mspConfig} isSidebarOpen={isSidebarOpen} />} />
            <Route path="/cloudmap" element={<MSPCloudMap mspConfig={mspConfig} isSidebarOpen={isSidebarOpen} />} />
            <Route path="/users" element={<MSPUsers users={users} mspConfig={mspConfig} isSidebarOpen={isSidebarOpen} />} />
            <Route path="/settings" element={<MSPSettings mspConfig={mspConfig} isSidebarOpen={isSidebarOpen} />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const MSPOverview = ({ tenants, users, mspConfig, isSidebarOpen }) => {
  const totalAwsAccounts = tenants.reduce((sum, tenant) => {
    const cloudAccounts = tenant.cloudAccounts || {};
    const standaloneCount = (cloudAccounts.aws || []).length;
    const orgCount = (cloudAccounts.organizations || []).reduce((orgSum, org) => {
      return orgSum + 1 + (org.memberAccounts || []).filter(m => m.connected).length;
    }, 0);
    return sum + standaloneCount + orgCount;
  }, 0);

  return (
    <div className="container-fluid p-4">
      <h2 className="mb-4">MSP Overview</h2>
      <div className="row">
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">Managed Tenants</h6>
              <h3 className="text-primary">{tenants.length}</h3>
              <p className="text-muted mb-0">Active customer accounts</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">MSP Users</h6>
              <h3 className="text-success">{users.length}</h3>
              <p className="text-muted mb-0">Team members</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">Active Tenants</h6>
              <h3 className="text-info">{tenants.filter(t => t.subscriptionPlan?.status === 'active').length}</h3>
              <p className="text-muted mb-0">With active subscriptions</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">Total AWS Accounts</h6>
              <h3 className="text-warning">{totalAwsAccounts}</h3>
              <p className="text-muted mb-0">Across all tenants</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MSPTenants = ({ tenants, mspConfig, isSidebarOpen }) => (
  <div className="container-fluid p-4">
    <h2 className="mb-4">Managed Tenants</h2>
    <div className="card">
      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Company</th>
                <th>Email</th>
                <th>Tenant ID</th>
                <th>Plan</th>
                <th>Created</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(tenant => (
                <tr key={tenant.tenantID}>
                  <td><strong>{tenant.companyName || tenant.email.split('@')[0]}</strong></td>
                  <td>{tenant.email}</td>
                  <td><code className="small">{tenant.tenantID}</code></td>
                  <td><span className="badge bg-secondary">{tenant.subscriptionPlan?.plan || 'Basic'}</span></td>
                  <td>{new Date(tenant.createdAt).toLocaleDateString()}</td>
                  <td>
                    <span className="badge bg-success">
                      {tenant.subscriptionPlan?.status || 'active'}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary">View Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

const MSPUsers = ({ users, mspConfig, isSidebarOpen }) => {
  const [showInviteModal, setShowInviteModal] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [accessLevel, setAccessLevel] = React.useState('technician');
  const [loading, setLoading] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState('');

  const handleInvite = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/msp/users/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mspID: mspConfig.mspID, email, accessLevel })
      });
      if (response.ok) {
        toastr.success('User invited successfully');
        setShowInviteModal(false);
        setEmail('');
        window.location.reload();
      } else {
        const error = await response.json();
        toastr.error(error.error || 'Failed to invite user');
      }
    } catch (error) {
      toastr.error('Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${userEmail}?`)) return;
    
    setDeleteLoading(userEmail);
    try {
      const response = await fetch(`https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/msp/users?email=${encodeURIComponent(userEmail)}&mspID=${mspConfig.mspID}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toastr.success('User deleted successfully');
        window.location.reload();
      } else {
        const error = await response.json();
        toastr.error(error.error || 'Failed to delete user');
      }
    } catch (error) {
      toastr.error('Failed to delete user');
    } finally {
      setDeleteLoading('');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { class: 'bg-success', text: 'Active' },
      'pending_password': { class: 'bg-warning text-dark', text: 'Pending Password' },
      'invited': { class: 'bg-secondary', text: 'Invited' },
      'unconfirmed': { class: 'bg-info', text: 'Unconfirmed' },
      'deleted': { class: 'bg-danger', text: 'Deleted' },
      'error': { class: 'bg-dark', text: 'Error' }
    };
    const config = statusMap[status] || { class: 'bg-secondary', text: status };
    return <span className={`badge ${config.class}`} style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{config.text}</span>;
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>MSP Users</h2>
        <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
          <i className="fas fa-plus me-2"></i>Invite User
        </button>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Invited</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.email}>
                    <td>{user.email}</td>
                          <td>
                            <span className="badge" style={{ 
                              backgroundColor: user.role === 'owner' ? '#0d6efd' : '#6c757d',
                              color: '#fff',
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                              minWidth: 'fit-content'
                            }}>
                              {user.role}
                            </span>
                          </td>
                          <td>
                            <span className="badge" style={{
                              backgroundColor: user.status === 'active' ? '#198754' : 
                                             user.status === 'pending_password' ? '#ffc107' : '#6c757d',
                              color: user.status === 'pending_password' ? '#000' : '#fff',
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                              minWidth: 'fit-content'
                            }}>
                              {user.status === 'pending_password' ? 'Pending Password' : 
                               user.status === 'active' ? 'Active' :
                               user.status === 'invited' ? 'Invited' :
                               user.status === 'unconfirmed' ? 'Unconfirmed' :
                               user.status === 'deleted' ? 'Deleted' :
                               user.status === 'error' ? 'Error' : user.status}
                            </span>
                          </td>
                    <td>{new Date(user.invitedAt).toLocaleDateString()}</td>
                    <td>
                      {user.role !== 'owner' && (
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(user.email)}
                          disabled={deleteLoading === user.email}
                        >
                          {deleteLoading === user.email ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showInviteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Invite MSP User</h5>
                <button className="btn-close" onClick={() => setShowInviteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Access Level</label>
                  <select className="form-select" value={accessLevel} onChange={(e) => setAccessLevel(e.target.value)}>
                    <option value="viewer">Viewer</option>
                    <option value="technician">Technician</option>
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleInvite} disabled={loading || !email}>
                  {loading ? 'Inviting...' : 'Invite'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MSPSettings = ({ mspConfig, isSidebarOpen }) => (
  <div className="container-fluid p-4">
    <h2 className="mb-4">MSP Settings</h2>
    <div className="card">
      <div className="card-body">
        <h5 className="mb-3">MSP Information</h5>
        <div className="row">
          <div className="col-md-6">
            <p><strong>Name:</strong> {mspConfig.name}</p>
            <p><strong>Slug:</strong> {mspConfig.slug}</p>
            <p><strong>Subdomain:</strong> {mspConfig.subdomain}</p>
          </div>
          <div className="col-md-6">
            <p><strong>MSP ID:</strong> <code>{mspConfig.mspID}</code></p>
            <p><strong>Status:</strong> <span className="badge bg-success">{mspConfig.status}</span></p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default MSPDashboard;
